import { useState } from "react";
import useConsumption from "../api/hooks/useConsumption";
import { FetchConsumptionDataInput } from "../api/types";

/**
 * Same as `useConsumption`, but nothing is fetched until the user asks for it.
 *
 * Consumption queries are by far the most expensive thing the backend does, and every
 * diagram used to fire one on mount — including one per card on the property and apartment
 * list pages, where most of them are never looked at. Deferring the request until the user
 * presses "show diagram" removes that cost from the initial page load entirely.
 *
 * That cost only exists where several diagrams share a page. A page showing a single
 * diagram passes `autoLoad` and behaves exactly like `useConsumption` again — the one
 * query is the reason the user opened the page, so making them click for it is pure
 * friction.
 *
 * Once requested, the query behaves exactly like `useConsumption`, react-query cache
 * included: changing the filters refetches, and going back to a diagram that was already
 * shown serves it from cache.
 *
 * @param input The consumption query input, unchanged from `useConsumption`.
 * @param canFetch Whether the input is complete enough to query at all — the same condition
 * previously passed to `useConsumption` as `enabled`. Defaults to true.
 * @param autoLoad True when this is the only diagram on the page, which fetches right away
 * without waiting for the user. Read on every render, so list pages can pass a count that
 * is only known once their own query has resolved. Defaults to false.
 * @returns The react-query result, plus `requested` and the `showDiagram` trigger.
 */
export default function useDeferredConsumption(
  input: Partial<FetchConsumptionDataInput>,
  canFetch: boolean = true,
  autoLoad: boolean = false
) {
  const [manuallyRequested, setManuallyRequested] = useState(false);

  const requested = autoLoad || manuallyRequested;

  const query = useConsumption(input, canFetch && requested);

  return {
    ...query,
    /** True once the diagram has been asked for, by the user or by `autoLoad`. */
    requested,
    /** Triggers the fetch. Safe to call more than once. */
    showDiagram: () => setManuallyRequested(true),
  };
}
